/**
 * Fetches remote files (certificates, chat PDF attachments) into memory once and
 * hands out copies of the bytes.
 *
 * Two callers want the same file: the pdf.js viewer and the save/share action.
 * Fetching per caller would download a 5MB certificate twice on a phone, so the
 * raw ArrayBuffer is cached here.
 *
 * IMPORTANT: pdf.js *detaches* (neuters) any ArrayBuffer handed to it, which would
 * empty the cache entry and leave every later read blank. `loadFileBytes` therefore
 * always returns a fresh copy and the cached buffer is never exposed directly.
 */

import { logger } from '@/utils/logger';

/** Keep the cache small — these are documents a user opened, not a CDN. */
const MAX_CACHED_ENTRIES = 8;
const MAX_CACHED_BYTES = 24 * 1024 * 1024;

const cache = new Map<string, ArrayBuffer>();
const inFlight = new Map<string, Promise<ArrayBuffer>>();

function cachedBytes(): number {
  let total = 0;
  for (const buffer of cache.values()) total += buffer.byteLength;
  return total;
}

/** Map insertion order is stable, so the first key is the oldest entry. */
function evictUntilWithinLimits(): void {
  while (cache.size > MAX_CACHED_ENTRIES || (cache.size > 1 && cachedBytes() > MAX_CACHED_BYTES)) {
    const oldest = cache.keys().next();
    if (oldest.done) return;
    cache.delete(oldest.value);
  }
}

async function fetchIntoCache(url: string): Promise<ArrayBuffer> {
  const response = await fetch(url, { credentials: 'omit' });
  if (!response.ok) {
    throw new Error(`Failed to download file (${response.status} ${response.statusText})`);
  }
  const buffer = await response.arrayBuffer();
  cache.set(url, buffer);
  evictUntilWithinLimits();
  return buffer;
}

/**
 * Downloads `url` (or reuses the cached bytes) and returns a private copy that the
 * caller may transfer, detach or mutate freely.
 */
export async function loadFileBytes(url: string): Promise<Uint8Array> {
  const cached = cache.get(url);
  // A detached buffer reports byteLength 0 — treat it as a miss and refetch.
  if (cached && cached.byteLength > 0) {
    return new Uint8Array(cached.slice(0));
  }
  cache.delete(url);

  let pending = inFlight.get(url);
  if (!pending) {
    pending = fetchIntoCache(url).finally(() => {
      inFlight.delete(url);
    });
    inFlight.set(url, pending);
  }

  try {
    const buffer = await pending;
    return new Uint8Array(buffer.slice(0));
  } catch (err) {
    logger.error('[pdfFile] failed to load file bytes', { url, err });
    throw err;
  }
}

/** Test/logout hook — drops every cached document. */
export function clearFileBytesCache(): void {
  cache.clear();
  inFlight.clear();
}
